import type { ReactElement } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { adminPromoCreateInput } from '@xdocs/schemas';
import type { AdminPromoCreateInput, AdminPromoUpdateInput, PromoCode } from '@xdocs/schemas';
import { Button, Input, Modal, Select, cn, useI18n } from '@xdocs/ui';
import { requestTyped } from '@xdocs/ws-protocol';
import { applyWsAlert } from '../../../lib/applyWsAlert.js';
import { safeFieldError } from '../../../i18n/safeT.js';
import { optionalProp } from '../../../lib/optionalProp.js';
import { useWs } from '../../../ws/WsProvider.js';
import { usePromosT } from './usePromosT.js';
import styles from './PromoFormDialog.module.css';

export interface PromoFormDialogProps {
  /** `null` when creating; a row when editing. */
  editingPromo: PromoCode | null;
  open: boolean;
  onClose: () => void;
  onSaved: (promo: PromoCode) => void;
}

function toDateInputValue(value: Date | null | undefined): string {
  if (!value) return '';
  const iso = value.toISOString();
  return iso.slice(0, 16); // yyyy-MM-ddTHH:mm for <input type="datetime-local">
}

/** `adminPromoCreateInput.perUserLimit` carries `.default(1)`, so zod's INPUT
 * type (pre-default, what `useForm`'s field-values generic must match) has
 * it optional, while the OUTPUT type (`AdminPromoCreateInput`, post-default)
 * requires it — same 3-generic `useForm` split documented in
 * `routes/explorer/NewFolderDialog.tsx`. */
type PromoFormValues = Omit<AdminPromoCreateInput, 'perUserLimit'> & { perUserLimit?: number | undefined };

/**
 * F55 create/edit form — a single form component handling BOTH modes since
 * `adminPromoUpdateInput` is `adminPromoCreateInput.partial().extend({promoCodeId})`
 * (`packages/schemas/src/billing.dto.ts`), so the create shape validates
 * cleanly as a stricter subset of the edit shape. Zod-validated (E6):
 * inline field errors via RHF, submit disabled while `isSubmitting`.
 */
export function PromoFormDialog({ editingPromo, open, onClose, onSaved }: PromoFormDialogProps): ReactElement {
  const { locale } = useI18n();
  const { t } = usePromosT();
  const { wsClient } = useWs();
  const isEdit = editingPromo !== null;

  const {
    register,
    handleSubmit,
    control,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<PromoFormValues, unknown, AdminPromoCreateInput>({
    resolver: zodResolver(adminPromoCreateInput),
    defaultValues: editingPromo
      ? {
          code: editingPromo.code,
          kind: editingPromo.kind,
          valueNumeric: editingPromo.valueNumeric,
          ...optionalProp('currency', editingPromo.currency ?? undefined),
          maxRedemptions: editingPromo.maxRedemptions,
          perUserLimit: editingPromo.perUserLimit,
          validFrom: editingPromo.validFrom,
          validUntil: editingPromo.validUntil,
          appliesTo: editingPromo.appliesTo,
          ...optionalProp('appliesToRefId', editingPromo.appliesToRefId ?? undefined),
        }
      : { kind: 'percent_off', appliesTo: 'any', perUserLimit: 1 },
  });

  // `useWatch` (not `formMethods.watch()`) — React Compiler flags `watch()`
  // as an unmemoizable API (`react-hooks/incompatible-library`); `useWatch`
  // is the compiler-friendly RHF v7 equivalent.
  const kind = useWatch({ control, name: 'kind' });

  async function onSubmit(values: AdminPromoCreateInput): Promise<void> {
    try {
      const promo = editingPromo
        ? await requestTyped(wsClient, 'admin.promo.update', {
            ...values,
            promoCodeId: editingPromo.id,
          } satisfies AdminPromoUpdateInput)
        : await requestTyped(wsClient, 'admin.promo.create', values);
      onSaved(promo);
    } catch (err) {
      applyWsAlert(err, setError, locale);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? t('adminPromos:form.title.edit') : t('adminPromos:form.title.create')} size="md" scrollableBody>
      <form onSubmit={(event) => void handleSubmit(onSubmit)(event)} noValidate className={cn(styles['form'])}>
        <Input
          label={t('adminPromos:form.field.code')}
          hint={t('adminPromos:form.field.code.hint')}
          required
          {...optionalProp('error', safeFieldError(locale, errors.code?.message))}
          {...register('code')}
        />

        <Select
          label={t('adminPromos:form.field.kind')}
          required
          options={[
            { value: 'percent_off', label: t('adminPromos:kind.percent_off') },
            { value: 'fixed_off', label: t('adminPromos:kind.fixed_off') },
            { value: 'bonus_credits', label: t('adminPromos:kind.bonus_credits') },
          ]}
          {...optionalProp('error', safeFieldError(locale, errors.kind?.message))}
          {...register('kind')}
        />

        <Input
          type="number"
          step="any"
          label={
            kind === 'fixed_off'
              ? t('adminPromos:form.field.value.fixed')
              : kind === 'bonus_credits'
                ? t('adminPromos:form.field.value.credits')
                : t('adminPromos:form.field.value.percent')
          }
          required
          {...optionalProp('error', safeFieldError(locale, errors.valueNumeric?.message))}
          {...register('valueNumeric', { valueAsNumber: true })}
        />

        {kind === 'fixed_off' && (
          <Input
            label={t('adminPromos:form.field.currency')}
            placeholder="USD" // i18n-exempt: ISO 4217 currency code example, not translatable prose
            {...optionalProp('error', safeFieldError(locale, errors.currency?.message))}
            {...register('currency')}
          />
        )}

        <div className={cn(styles['row'])}>
          <Input
            type="number"
            label={t('adminPromos:form.field.maxRedemptions')}
            hint={t('adminPromos:form.field.maxRedemptions.hint')}
            {...optionalProp('error', safeFieldError(locale, errors.maxRedemptions?.message))}
            {...register('maxRedemptions', { setValueAs: (v: string) => (v === '' ? null : Number(v)) })}
          />
          <Input
            type="number"
            label={t('adminPromos:form.field.perUserLimit')}
            required
            {...optionalProp('error', safeFieldError(locale, errors.perUserLimit?.message))}
            {...register('perUserLimit', { valueAsNumber: true })}
          />
        </div>

        <div className={cn(styles['row'])}>
          <Input
            type="datetime-local"
            label={t('adminPromos:form.field.validFrom')}
            defaultValue={toDateInputValue(editingPromo?.validFrom)}
            {...optionalProp('error', safeFieldError(locale, errors.validFrom?.message))}
            {...register('validFrom', { valueAsDate: true })}
          />
          <Input
            type="datetime-local"
            label={t('adminPromos:form.field.validUntil')}
            defaultValue={toDateInputValue(editingPromo?.validUntil)}
            {...optionalProp('error', safeFieldError(locale, errors.validUntil?.message))}
            {...register('validUntil', { valueAsDate: true })}
          />
        </div>

        <Select
          label={t('adminPromos:form.field.appliesTo')}
          required
          options={[
            { value: 'any', label: t('adminPromos:appliesTo.any') },
            { value: 'subscription', label: t('adminPromos:appliesTo.subscription') },
            { value: 'credit_pack', label: t('adminPromos:appliesTo.credit_pack') },
          ]}
          {...optionalProp('error', safeFieldError(locale, errors.appliesTo?.message))}
          {...register('appliesTo')}
        />

        <div className={cn(styles['actions'])}>
          <Button variant="secondary" type="button" onClick={onClose} disabled={isSubmitting}>
            {t('adminPromos:form.action.cancel')}
          </Button>
          <Button variant="primary" type="submit" state={isSubmitting ? 'loading' : 'idle'}>
            {t('adminPromos:form.action.save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
